import { api } from "./client";

export type IssueStatus = "OPEN" | "IN_PROGRESS" | "UNDER_REVIEW" | "RESOLVED" | "CLOSED" | "WONT_FIX" | "ARCHIVED";
export type IssueType = "BUG" | "FEATURE_REQUEST" | "APP_ISSUE" | "OTHER";
export type IssuePriority = "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";

export interface IssueUser {
  id: string;
  email: string;
  displayName: string | null;
}

export interface IssueApp {
  id: string;
  name: string;
}

export interface IssueComment {
  id: string;
  issueId: string;
  authorId: string;
  author: IssueUser;
  body: string;
  isInternal: boolean;
  editedById: string | null;
  editedBy: IssueUser | null;
  editedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Issue {
  id: string;
  title: string;
  body: string;
  type: IssueType;
  status: IssueStatus;
  priority: IssuePriority;
  submittedById: string;
  submittedBy: IssueUser;
  assignedToId: string | null;
  assignedTo: IssueUser | null;
  appId: string | null;
  app: IssueApp | null;
  comments: IssueComment[];
  _count?: { comments: number };
  createdAt: string;
  updatedAt: string;
}

export interface IssueList {
  total: number;
  page: number;
  limit: number;
  issues: Issue[];
}

export interface ListIssuesParams {
  status?: IssueStatus;
  statuses?: IssueStatus[];
  type?: IssueType;
  priority?: IssuePriority;
  assignedToId?: string;
  submittedById?: string;
  includeArchived?: boolean;
  page?: number;
  limit?: number;
}

export interface CreateIssueInput {
  title: string;
  body: string;
  type: IssueType;
  appId?: string;
  status?: IssueStatus;
  priority?: IssuePriority;
  assignedToId?: string | null;
}

export interface UpdateIssueInput {
  title?: string;
  body?: string;
  type?: IssueType;
  status?: IssueStatus;
  priority?: IssuePriority;
  assignedToId?: string | null;
}

export interface CreateCommentInput {
  body: string;
  isInternal?: boolean;
}

export interface IssueTypeConfig {
  id: string;
  type: IssueType;
  label: string;
  description: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateIssueTypeConfigInput {
  label?: string;
  description?: string | null;
  sortOrder?: number;
  isActive?: boolean;
}

export const issueTypeConfigsApi = {
  list: () => api.get<IssueTypeConfig[]>("/issue-type-configs").then((r) => r.data),

  update: (type: IssueType, input: UpdateIssueTypeConfigInput) =>
    api.patch<IssueTypeConfig>(`/issue-type-configs/${type}`, input).then((r) => r.data),

  reset: (type: IssueType) =>
    api.post<IssueTypeConfig>(`/issue-type-configs/${type}/reset`).then((r) => r.data),
};

export const issuesApi = {
  create: (input: CreateIssueInput) =>
    api.post<Issue>("/issues", input).then((r) => r.data),

  list: (params: ListIssuesParams = {}) =>
    api.get<IssueList>("/issues", { params }).then((r) => r.data),

  get: (issueId: string) =>
    api.get<Issue>(`/issues/${issueId}`).then((r) => r.data),

  update: (issueId: string, input: UpdateIssueInput) =>
    api.patch<Issue>(`/issues/${issueId}`, input).then((r) => r.data),

  remove: (issueId: string) =>
    api.delete(`/issues/${issueId}`).then(() => undefined),

  addComment: (issueId: string, input: CreateCommentInput) =>
    api.post<IssueComment>(`/issues/${issueId}/comments`, input).then((r) => r.data),

  updateComment: (issueId: string, commentId: string, body: string, isInternal?: boolean) =>
    api.patch<IssueComment>(`/issues/${issueId}/comments/${commentId}`, { body, ...(isInternal !== undefined && { isInternal }) }).then((r) => r.data),
};
